/* ProfileGuide — patient-friendly tour за меню Профил. */
import React from "react";
import {
  User, CreditCard, BarChart3, FileText, Shield, Bell, Download,
} from "lucide-react";
import MenuGuide from "@/components/guides/MenuGuide";

const MODULES = [
  {
    id: "completeness",
    icon: User, color: "#10B981",
    name_bg: "Попълненост на профила",
    name_en: "Profile completeness",
    purpose_bg: "Показва колко от данните сте попълнили — име, тегло, цели, лекарства. По-пълен профил означава по-точни препоръки.",
    purpose_en: "Shows how much of your data is filled in — name, weight, goals, medications. A fuller profile = more accurate AI recommendations.",
    benefit_bg: "Ако пропуснете нещо важно (например лекарство), съветът не може да бъде безопасен. Затова попълнете всичко.",
    benefit_en: "If you skip something important (e.g. medications), the AI can't give you safe advice. So fill it all in.",
    action_bg: "Натиснете „Редактиране“ → попълнете липсващите полета → запазете.",
    action_en: "Tap Edit -> fill in missing fields -> save.",
    jumpId: null,
  },
  {
    id: "subscription",
    icon: CreditCard, color: "#8B5CF6",
    name_bg: "Абонамент и кредити",
    name_en: "Subscription & credits",
    purpose_bg: "Виждате сегашния си план (безплатен, пробен, Pro или за клиника) и колко запитвания Ви остават.",
    purpose_en: "See your current plan (Free / Trial / Pro / Clinic) and how many AI requests remain.",
    benefit_bg: "При смяна на месеца неизползваните кредити се прехвърлят — до двойно на месечния брой.",
    benefit_en: "When the month resets, unused credits roll over (up to 2x the monthly limit).",
    action_bg: "Натиснете „Управление“ → вижте пакетите → при нужда преминете към по-висок.",
    action_en: "Tap Manage -> see packages -> upgrade if needed.",
    jumpId: null,
  },
  {
    id: "weekly_report",
    icon: BarChart3, color: "#06B6D4",
    name_bg: "Седмичен отчет",
    name_en: "Weekly AI report",
    aiPowered: true,
    purpose_bg: "Обобщение на седмицата: какво сте яли, как сте спали, как сте се движили. Откроява това, което се повтаря.",
    purpose_en: "A summary of your week: what you ate, how you slept, how you trained. AI surfaces patterns that repeat.",
    benefit_bg: "Виждате отстрани кои навици работят и кои не.",
    benefit_en: "You see at a glance which habits work — and which don't.",
    action_bg: "Превъртете надолу до „Седмичен отчет“.",
    action_en: "Scroll to the Weekly AI report section.",
    jumpId: "profile-weekly-report",
  },
  {
    id: "daily_note",
    icon: FileText, color: "#F59E0B",
    name_bg: "Дневна бележка",
    name_en: "Daily note",
    purpose_bg: "Кратка лична бележка за деня — настроение, оплакване, важно събитие. Не е дневник на чувства, а обстановка за разчитането.",
    purpose_en: "A short personal note for the day — mood, symptom, important event. Not an emotion journal, but context for AI.",
    benefit_bg: "Когато Ви предлагаме храна или режим, отчитаме и тези бележки. Така препоръките са по-човешки.",
    benefit_en: "When AI suggests food or routine, it factors in these notes. Recommendations become more human.",
    action_bg: "Превъртете до „Дневна бележка“ → пишете свободно.",
    action_en: "Scroll to Daily note -> write freely.",
    jumpId: "profile-daily-note",
  },
  {
    id: "trial",
    icon: Bell, color: "#A855F7",
    name_bg: "Табло на пробния период",
    name_en: "Trial dashboard",
    purpose_bg: "Ако сте в пробен период — виждате кои раздели сте пробвали, колко дни остават и колко запитвания имате.",
    purpose_en: "If you're on trial — see which modules you've tried, days remaining, AI requests you have.",
    benefit_bg: "Помага да изпробвате всичко важно, преди да решите дали да продължите.",
    benefit_en: "Helps you try everything important before deciding to continue.",
    action_bg: "Лявата колона на профила (вижда се само през пробния период).",
    action_en: "Left column of profile (visible only during trial).",
    jumpId: null,
  },
  {
    id: "security",
    icon: Shield, color: "#0EA5E9",
    name_bg: "Сигурност",
    name_en: "Security",
    purpose_bg: "Промяна на парола, втора стъпка при влизане (2FA), отворени сесии.",
    purpose_en: "Change password, two-factor authentication (2FA), active sessions.",
    benefit_bg: "Защитавате медицинските си данни. Втората стъпка е втори ключ — дори да откраднат паролата Ви, не могат да влязат.",
    benefit_en: "Protect your health data. 2FA adds a second key — even if someone steals your password, they can't log in.",
    action_bg: "Профил → „Сигурност“ → включете втората стъпка.",
    action_en: "Profile -> Security -> enable 2FA.",
    jumpId: null,
  },
  {
    id: "export",
    icon: Download, color: "#EC4899",
    name_bg: "Изнасяне на данните (GDPR)",
    name_en: "Data export (GDPR)",
    purpose_bg: "По всяко време можете да изтеглите всичките си данни (хранения, тегло, бележки, разговори) като файл JSON или PDF.",
    purpose_en: "At any time you can download all your data (meals, weight, notes, AI chats) as JSON or PDF.",
    benefit_bg: "Това е Ваше право по GDPR. Можете и да поискате пълно изтриване — без пречки.",
    benefit_en: "It's your right under GDPR. You can also request full deletion — no barriers.",
    action_bg: "Профил → „Изнасяне на данните“ → изберете вид файл → изтеглете.",
    action_en: "Profile -> Data export section -> choose format -> download.",
    jumpId: null,
  },
];

export default function ProfileGuide() {
  return (
    <MenuGuide
      modules={MODULES}
      storageKey="profile_guide_seen"
      triggerLabelBg="Ръководство"
      triggerLabelEn="Guide"
      triggerColorClass="from-emerald-500 to-teal-500"
      testIdPrefix="profile-guide"
    />
  );
}
